import { useState } from "react";
import { supabase } from "../../lib/supabase";
import { readInvitation, readSoundtrack, validDate, validTime } from "../../lib/invitation";
import type { Registry, RegistryEvent } from "../../types/registry";
import type { ThemeConfig } from "../../types/theme";
import InvitationSettings from "./InvitationSettings";
export default function OwnerInvitationSettings({
  registry,
  events,
  onSaved,
  giftRegistryAvailable,
}: {
  registry: Registry;
  events: RegistryEvent[];
  giftRegistryAvailable: boolean;
  onSaved: (theme: ThemeConfig) => void;
}) {
  const [value, setValue] = useState(() =>
    readInvitation(registry.theme?.invitation),
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false),
    [message, setMessage] = useState("");
  const save = async () => {
    if (uploading) return;
    setSaving(true);
    setMessage("");
    try {
      if (
        value &&
        (value.timeline.some(
          (t) =>
            !t.eventId &&
            (!t.title.trim() ||
              !validTime(t.time) ||
              (t.date && !validDate(t.date))),
        ) ||
          (value.rsvpDeadline &&
            (!validDate(value.rsvpDeadline) ||
              value.rsvpDeadline > registry.event_date)))
      )
        throw new Error(
          "Check the activity names, dates, times and RSVP deadline.",
        );
      if (value?.soundtrackUrl?.trim() && !readSoundtrack(value.soundtrackUrl)) throw new Error("Check the soundtrack link before saving.");
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || user.id !== registry.user_id)
        throw new Error("Only the registry owner can change the invitation.");
      const { data, error } = await supabase
        .from("registries")
        .select("theme")
        .eq("id", registry.id)
        .eq("user_id", user.id)
        .single();
      if (error) throw error;
      const theme = { ...(data.theme || {}) };
      if (value) theme.invitation = { ...value, soundtrackUrl: readSoundtrack(value.soundtrackUrl)?.url || "" };
      else delete theme.invitation;
      const { error: saveError } = await supabase
        .from("registries")
        .update({ theme })
        .eq("id", registry.id)
        .eq("user_id", user.id)
        .select("id")
        .single();
      if (saveError) throw saveError;
      onSaved(theme);
      setMessage(
        "Invitation saved. Your existing invitation link now uses this design.",
      );
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Unable to save. Your current invitation has not been changed.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-8 space-y-3">
      <fieldset disabled={saving}>
        <InvitationSettings
          onUploadStateChange={setUploading}
          value={value}
          onChange={setValue}
          registry={registry}
          events={events}
          giftRegistryAvailable={giftRegistryAvailable}
        />
      </fieldset>
      <button
        type="button"
        disabled={saving || uploading}
        onClick={save}
        className="bg-rose-700 text-white px-5 py-2 rounded disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save invitation design"}
      </button>
      {message && (
        <p role="status" className="text-sm bg-white p-3 rounded text-gray-800">
          {message}
        </p>
      )}
    </div>
  );
}
